import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type AuthBenefitIcon = 'orders' | 'heart' | 'star' | 'checkout';

export interface AuthBenefit {
  icon: AuthBenefitIcon;
  label: string;
  description: string;
}

interface AuthLayoutProps {
  heading: ReactNode;
  description: string;
  benefits: AuthBenefit[];
  children: ReactNode;
}

const BENEFIT_ICON_PATHS: Record<AuthBenefitIcon, string> = {
  orders:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  heart:
    'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  star:
    'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z',
  checkout:
    'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
};

export function AuthLayout({ heading, description, benefits, children }: AuthLayoutProps) {
  return (
    <div className="min-h-[calc(100vh-64px)] md:min-h-[calc(100vh-72px)] flex -mb-16">
      <aside className="hidden lg:flex flex-col justify-center w-[44%] px-12 xl:px-16 py-14 bg-gradient-to-br from-champagne via-champagne to-champagne-dark">
        <div className="max-w-md">
          <Link to="/" aria-label="Baby Chic, ir para a página inicial" className="inline-flex items-center gap-3 mb-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chocolate/40">
            <img src="/images/categories/icon-7.webp" alt="" className="w-14 h-14 shrink-0" />
            <span className="font-brand text-4xl text-chocolate-dark leading-none">Baby Chic</span>
          </Link>
          <h2 className="font-display text-3xl xl:text-4xl font-bold text-text-dark leading-tight text-balance">{heading}</h2>
          <p className="mt-4 text-base text-text-medium leading-relaxed">{description}</p>

          <ul className="mt-10 space-y-5">
            {benefits.map((b) => (
              <li key={b.label} className="flex items-start gap-3.5">
                <span className="w-10 h-10 shrink-0 rounded-full bg-white/80 border border-champagne-dark flex items-center justify-center text-chocolate-dark">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d={BENEFIT_ICON_PATHS[b.icon]} />
                  </svg>
                </span>
                <div className="pt-0.5">
                  <p className="text-base font-semibold text-text-dark leading-snug">{b.label}</p>
                  <p className="text-sm text-text-medium">{b.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <div className="flex-1 flex items-start lg:items-center justify-center px-5 sm:px-6 pt-8 pb-16 sm:pt-12 lg:py-12 bg-champagne-light">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}
